import { useEffect, useRef, useState, type CSSProperties } from 'react'
import {
  DUCKED_VOLUME,
  RETRY_AFTER_ALL_FAILED_MS,
  STALL_CHECK_MS,
  advance,
  failed,
  filmWatchArmed,
  itemMs,
  objectFitOf,
  playlistKey,
  retry,
  startFilm,
  startOf,
  watchFilm,
  type FilmWatch,
} from './playlist'
import type { LayoutElement } from './types'
import { callSounding, onCallSounding } from '../lib/voice'
import { skipWorthSaying } from '../lib/mediaSkips'
import { useStaffText } from '../ui/StaffText'

/**
 * A playlist on a hall board: pictures and films, one after another, in a loop.
 *
 * Films are muted unless the designer turned an item's Sound on. The board's sound
 * belongs to the calls, so a film with sound drops to DUCKED_VOLUME while a call is
 * chimed or read out. A browser that refuses sound before the board is pressed gets
 * the film muted, and the next press or key turns it up.
 *
 * The rules for moving on live in playlist.ts, where they are tested; this file
 * only wires them to timers and to the browser's events. The server refuses a
 * playlist that covers the calls, and the call page is drawn above the whole
 * canvas, so nothing here needs to know about either.
 *
 * In the editor (preview) the first item is shown still and silent, so a designer
 * sees what the box holds without a film running while they work; the editor's
 * Preview plays, with sound, since opening it was a press.
 */
export function MediaPlayer({
  element,
  assetURL,
  preview,
  play,
  onSkip,
}: {
  element: LayoutElement
  assetURL?: (assetId: string) => string
  preview?: boolean
  /** A preview that plays the list as a wall would (the editor's Preview). */
  play?: boolean
  onSkip?: (assetId: string, kind: 'image' | 'video') => void
}) {
  const still = preview && !play
  const items = element.media?.items ?? []
  const count = items.length
  const key = playlistKey(items)

  const [stored, setPlace] = useState(() => startOf(key))
  // A new list — a new version, or a new time of day with different items — starts
  // at its beginning with nothing held against it. Decided while rendering rather
  // than in an effect, so the old place is never drawn against the new list. The
  // same list in a new document (a logo moved, or the midnight hand-over between two
  // halves of one late-night time) has the same key and carries on where it was.
  let place = stored
  if (stored.key !== key) {
    place = startOf(key)
    setPlace(place)
  }
  const { index, round, resting, attempt } = place
  const item = resting ? undefined : items[index]

  // A lone film loops by itself, with no gap; its seconds (filled from its length) are not a cut.
  const looping = !still && count === 1 && item?.kind === 'video'
  const ms = item ? itemMs(item) : 0
  const showing = item !== undefined

  // Moving on by the clock: a picture's time, a film's cut, or the upper bound for a
  // film longer than anything a hall should loop. A looping film has no clock:
  // cutting it at the bound would restart it mid-loop for nothing. A film that
  // stops moving is the Film's own watchdog's business, not this timer's.
  useEffect(() => {
    if (still || !showing || looping) return
    const timer = window.setTimeout(() => {
      setPlace((p) => (p.key === key ? advance(p, count, round) : p))
    }, ms)
    return () => window.clearTimeout(timer)
    // Keyed on what the item is (its time, its list, its round), not on the item
    // object: a new document hands over new objects for the same list, and
    // restarting a picture's time for that would stretch it.
  }, [still, showing, looping, ms, key, count, round])

  // Nothing would load: rest, then try the whole list again. A board that has lost
  // the server gets it back without anybody touching it.
  useEffect(() => {
    if (!resting) return
    const timer = window.setTimeout(() => {
      setPlace((p) => (p.key === key ? retry(p) : p))
    }, RETRY_AFTER_ALL_FAILED_MS)
    return () => window.clearTimeout(timer)
  }, [resting, key])

  if (!item || !assetURL) {
    // In the editor, before anything is chosen. On a wall an empty box — or one
    // resting because nothing loads — shows the design's own background, never a
    // broken-picture icon.
    return preview ? <EmptyPlaylist element={element} /> : null
  }

  const over = () => setPlace((p) => (p.key === key ? advance(p, count, round) : p))
  const broke = (code?: number) => {
    if (!preview && skipWorthSaying(navigator.onLine, code)) onSkip?.(item.assetId, item.kind === 'video' ? 'video' : 'image')
    setPlace((p) => (p.key === key ? failed(p, count, index, round) : p))
  }
  const style: CSSProperties = { width: '100%', height: '100%', objectFit: objectFitOf(item.fit) }
  const src = assetURL(item.assetId)

  if (item.kind === 'video') {
    return (
      <Film
        // Keyed with its round, so the same film shown again is a new element that
        // starts from its beginning (constraints: a film is keyed with its round).
        key={`${round}:${item.assetId}`}
        src={src}
        preview={still}
        loop={looping}
        sound={item.sound === true && !still}
        onEnded={over}
        onError={broke}
        style={style}
      />
    )
  }
  return <img key={`${attempt}:${index}:${item.assetId}`} src={src} alt="" onError={() => broke()} style={style} />
}

/**
 * An empty playlist in a preview: a quiet framed box that says what it is.
 *
 * Without it the gallery's tile of "Split with a playlist" and the editor's canvas
 * showed a blank hole where the playlist is, which reads as a design with a piece
 * missing rather than a place waiting for pictures. In the reader's language,
 * because it is somebody designing who reads it; a board never draws it. Sizes are
 * in canvas units and follow the box, like everything else on the canvas.
 */
function EmptyPlaylist({ element }: { element: LayoutElement }) {
  const { say } = useStaffText()
  const edge = Math.max(2, Math.round(Math.min(element.w, element.h) / 120))
  return (
    <div
      className="layout-playlist-empty"
      style={{
        borderWidth: edge,
        fontSize: Math.max(14, Math.round(Math.min(element.h * 0.1, element.w * 0.06))),
      }}
    >
      ▶ {say('designs.media.placeholder')}
    </div>
  )
}

/**
 * One showing of one film.
 *
 * The source is set and taken away by hand. A board runs for weeks and makes a new
 * <video> for every showing; a removed element that still holds its source keeps
 * its decoder and buffered data until the collector gets round to it, which on a
 * small panel is how a playlist becomes a crash on the third day. Emptying the
 * source and calling load() is the documented way to let them go at once. Setting
 * it in the effect, not as a prop, is what keeps StrictMode's mount, unmount and
 * mount again from leaving a film with no source.
 */
function Film({
  src,
  preview,
  loop,
  sound,
  onEnded,
  onError,
  style,
}: {
  src: string
  preview?: boolean
  loop: boolean
  /** Plays with its sound; muted otherwise. */
  sound: boolean
  onEnded: () => void
  /** With the film's MediaError code, when it has one. */
  onError: (code?: number) => void
  style: CSSProperties
}) {
  const ref = useRef<HTMLVideoElement>(null)
  // The latest handler, read by the watchdog, so a new render does not restart
  // the watch and reset how long the film has been standing still.
  const ended = useRef(onEnded)
  useEffect(() => {
    ended.current = onEnded
  }, [onEnded])

  useEffect(() => {
    const video = ref.current
    if (!video) return
    // React sets muted as a property and not always as the attribute, and the
    // attribute is what some browsers read when deciding whether a film may start
    // by itself. Both, then, before the source arrives.
    video.muted = !sound
    video.defaultMuted = !sound
    video.src = src
    let gone = false
    const unmute = () => {
      video.muted = false
      window.removeEventListener('pointerdown', unmute)
      window.removeEventListener('keydown', unmute)
    }
    if (!preview) {
      // autoPlay covers most browsers; asking as well covers a source set after
      // the element was made. A film that still does not start moves on by its clock.
      void startFilm(video, sound).then((how) => {
        if (gone || !sound || how !== 'muted') return
        // Sound refused before any press: the next press or key on the board turns it on.
        window.addEventListener('pointerdown', unmute)
        window.addEventListener('keydown', unmute)
      })
    }
    return () => {
      gone = true
      window.removeEventListener('pointerdown', unmute)
      window.removeEventListener('keydown', unmute)
      video.pause()
      video.removeAttribute('src')
      video.load()
    }
  }, [src, preview, sound])

  // Lower the film while a call is chimed or read out, and back after.
  useEffect(() => {
    const video = ref.current
    if (!video || !sound) return
    const duck = () => {
      video.volume = callSounding() ? DUCKED_VOLUME : 1
    }
    duck()
    return onCallSounding(duck)
  }, [sound])

  // The watchdog. A film that stops moving without an error (a dropped network, a
  // refused start) is treated as over, not as failed: the list moves on, and a lone
  // film is made again from a fresh request at once, rather than resting for half
  // a minute as a list that will not load does.
  //
  // It looks only once the film has played (its first 'playing' event), or once
  // it has had FILM_START_GRACE_MS to start, so a slow first buffer is not made
  // again every twenty-five seconds and never shown.
  useEffect(() => {
    if (preview) return
    const video = ref.current
    const mountedAt = Date.now()
    let played = false
    const onPlaying = () => {
      played = true
    }
    video?.addEventListener('playing', onPlaying)
    let watch: FilmWatch | undefined
    const timer = window.setInterval(() => {
      const video = ref.current
      if (!video || video.ended) return
      if (!filmWatchArmed(played, mountedAt, Date.now())) return
      watch = watchFilm(watch, video.currentTime, Date.now())
      if (watch.stalled) {
        window.clearInterval(timer)
        ended.current()
      }
    }, STALL_CHECK_MS)
    return () => {
      window.clearInterval(timer)
      video?.removeEventListener('playing', onPlaying)
    }
  }, [src, preview])

  return (
    <video
      ref={ref}
      muted={!sound}
      playsInline
      autoPlay={!preview}
      preload={preview ? 'metadata' : 'auto'}
      loop={loop}
      onEnded={onEnded}
      onError={() => onError(ref.current?.error?.code)}
      style={style}
    />
  )
}
